import { CalendarBooking, CalendarDay, Booking, ExternalBooking } from '../types';

// Color scheme for different booking statuses
export const BOOKING_COLORS = {
  confirmed: '#4caf50',    // Green
  pending: '#ff9800',      // Orange  
  cancelled: '#f44336',    // Red
  completed: '#2196f3',    // Blue
  external: '#9c27b0',     // Purple (Airbnb)
} as const;

/**
 * Assigns a display color to a booking based on its status and source
 */
export function assignBookingColor(booking: CalendarBooking): string {
  if (booking.isExternal || booking.source === 'airbnb') {
    return BOOKING_COLORS.external;
  }
  
  switch (booking.status) {
    case 'confirmed':
      return BOOKING_COLORS.confirmed;
    case 'pending':
      return BOOKING_COLORS.pending;
    case 'cancelled':
      return BOOKING_COLORS.cancelled;
    case 'completed':
      return BOOKING_COLORS.completed;
    default:
      return BOOKING_COLORS.pending;
  }
}

/**
 * Maps booking status to display text
 */
export function mapBookingStatusToText(status: Booking['status']): string {
  const statusMap = {
    confirmed: 'Confirmed',
    pending: 'Pending',
    cancelled: 'Cancelled',
    completed: 'Completed'
  };
  
  return statusMap[status] || 'Unknown';
}

/**
 * Converts a regular booking to a calendar booking with additional display properties
 */
export function convertToCalendarBooking(
  booking: Booking, 
  listingTitle: string, 
  isExternal: boolean = false,
  source: 'direct' | 'airbnb' = 'direct'
): CalendarBooking {
  const calendarBooking: CalendarBooking = {
    ...booking,
    listingTitle,
    isExternal,
    source,
    displayColor: ''
  };
  
  // Assign color after creating the object
  calendarBooking.displayColor = assignBookingColor(calendarBooking);
  
  return calendarBooking;
}

/**
 * Converts an external booking to a calendar booking
 */
export function convertExternalToCalendarBooking(
  externalBooking: ExternalBooking,
  listingTitle: string
): CalendarBooking {
  // Create a calendar booking from external booking data
  const calendarBooking: CalendarBooking = {
    bookingId: externalBooking.externalBookingId,
    listingId: externalBooking.listingId,
    hostId: '', // Not available for external bookings
    guestId: '', // Not available for external bookings
    startDate: externalBooking.startDate,
    endDate: externalBooking.endDate,
    guests: externalBooking.guests || 1,
    totalPrice: 0, // Not available for external bookings
    status: externalBooking.status as 'confirmed' | 'cancelled',
    paymentStatus: 'paid' as const, // Assume external bookings are paid
    primaryGuestContact: {
      email: '',
      preferredMethod: 'email' as const
    },
    expectedGuests: externalBooking.guests || 1,
    guestCheckInStatus: {
      completed: 0,
      pending: externalBooking.guests || 1,
      lastUpdated: externalBooking.syncedAt
    },
    communicationLog: [],
    createdAt: externalBooking.syncedAt,
    updatedAt: externalBooking.lastUpdated,
    listingTitle,
    isExternal: true,
    source: externalBooking.source,
    displayColor: ''
  };
  
  // Assign color after creating the object
  calendarBooking.displayColor = assignBookingColor(calendarBooking);
  
  return calendarBooking;
}

/**
 * Processes bookings to ensure they have display colors assigned
 */
export function processBookingsForCalendar(bookings: CalendarBooking[]): CalendarBooking[] {
  return bookings.map(booking => ({
    ...booking,
    displayColor: booking.displayColor || assignBookingColor(booking)
  }));
}

/**
 * Gets the start of the month for a given date
 */
export function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Gets the end of the month for a given date
 */
export function getMonthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/**
 * Gets the start of the calendar view (including previous month days)
 */
export function getCalendarStart(date: Date): Date {
  const monthStart = getMonthStart(date);
  const dayOfWeek = monthStart.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0
  
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(monthStart.getDate() - daysToSubtract);
  
  return calendarStart;
}

/**
 * Gets the end of the calendar view (including next month days)
 */
export function getCalendarEnd(date: Date): Date {
  const monthEnd = getMonthEnd(date);
  const dayOfWeek = monthEnd.getDay();
  const daysToAdd = dayOfWeek === 0 ? 0 : 7 - dayOfWeek; // Sunday = 6
  
  const calendarEnd = new Date(monthEnd);
  calendarEnd.setDate(monthEnd.getDate() + daysToAdd);
  
  return calendarEnd;
}

/**
 * Generates an array of dates for the calendar view (6 weeks)
 */
export function generateCalendarDates(currentMonth: Date): Date[] {
  const start = getCalendarStart(currentMonth);
  const dates: Date[] = [];
  
  // Generate 42 days (6 weeks)
  for (let i = 0; i < 42; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push(date);
  }
  
  return dates;
}

/**
 * Checks if a date is today
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Checks if a date is in the current month
 */
export function isCurrentMonth(date: Date, currentMonth: Date): boolean {
  return date.getMonth() === currentMonth.getMonth() && 
         date.getFullYear() === currentMonth.getFullYear();
}

/**
 * Checks if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return date1.toDateString() === date2.toDateString();
}

/**
 * Checks if a booking has a check-in on the given date
 */
export function hasCheckIn(booking: CalendarBooking, date: Date): boolean {
  const startDate = new Date(booking.startDate);
  return isSameDay(startDate, date);
}

/**
 * Checks if a booking has a check-out on the given date
 */
export function hasCheckOut(booking: CalendarBooking, date: Date): boolean {
  const endDate = new Date(booking.endDate);
  return isSameDay(endDate, date);
}

/**
 * Filters bookings for a specific date
 */
export function getBookingsForDate(bookings: CalendarBooking[], date: Date): CalendarBooking[] {
  return bookings.filter(booking => {
    const startDate = new Date(booking.startDate);
    const endDate = new Date(booking.endDate);
    
    // Check if the date falls within the booking period (inclusive of start, exclusive of end)
    return date >= startDate && date < endDate;
  });
}

/**
 * Creates a CalendarDay object for a given date
 */
export function createCalendarDay(
  date: Date,
  bookings: CalendarBooking[],
  currentMonth: Date,
  selectedDate: Date | null
): CalendarDay {
  const dayBookings = getBookingsForDate(bookings, date);
  
  return {
    date: new Date(date),
    bookings: dayBookings,
    hasCheckIn: dayBookings.some(booking => hasCheckIn(booking, date)),
    hasCheckOut: dayBookings.some(booking => hasCheckOut(booking, date)),
    isToday: isToday(date),
    isSelected: selectedDate ? isSameDay(date, selectedDate) : false,
    isCurrentMonth: isCurrentMonth(date, currentMonth)
  };
}

/**
 * Groups bookings by date for efficient calendar rendering
 */
export function groupBookingsByDate(bookings: CalendarBooking[]): Map<string, CalendarBooking[]> {
  const grouped = new Map<string, CalendarBooking[]>();
  
  bookings.forEach(booking => {
    const startDate = new Date(booking.startDate);
    const endDate = new Date(booking.endDate);
    
    // Add booking to each date it spans
    const currentDate = new Date(startDate);
    while (currentDate < endDate) {
      const dateKey = currentDate.toDateString();
      
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      
      grouped.get(dateKey)!.push(booking);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });
  
  return grouped;
}

/**
 * Formats a date for display in the calendar
 */
export function formatCalendarDate(date: Date): string {
  return date.getDate().toString();
}

/**
 * Formats a date for the day summary panel header
 */
export function formatDaySummaryDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Formats a month/year for the calendar header
 */
export function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long'
  });
}

/**
 * Gets the next month date
 */
export function getNextMonth(date: Date): Date {
  const nextMonth = new Date(date);
  nextMonth.setMonth(date.getMonth() + 1);
  return nextMonth;
}

/**
 * Gets the previous month date
 */
export function getPreviousMonth(date: Date): Date {
  const prevMonth = new Date(date);
  prevMonth.setMonth(date.getMonth() - 1);
  return prevMonth;
}

/**
 * Calculates the number of nights for a booking
 */
export function calculateBookingNights(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Checks if a booking spans multiple days
 */
export function isMultiDayBooking(booking: CalendarBooking): boolean {
  return calculateBookingNights(booking.startDate, booking.endDate) > 1;
}

/**
 * Merges direct bookings and external bookings into a single array
 */
export function mergeBookingsWithExternal(
  directBookings: CalendarBooking[],
  externalBookings: ExternalBooking[],
  listingTitles: Record<string, string>
): CalendarBooking[] {
  // Convert external bookings to calendar bookings
  const convertedExternalBookings = externalBookings.map(externalBooking => 
    convertExternalToCalendarBooking(
      externalBooking, 
      listingTitles[externalBooking.listingId] || `Listing ${externalBooking.listingId}`
    )
  );
  
  // Combine and sort by start date
  const allBookings = [...directBookings, ...convertedExternalBookings];
  
  return allBookings.sort((a, b) => 
    new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );
}

/**
 * Detects conflicts between direct and external bookings
 */
export function detectBookingConflicts(bookings: CalendarBooking[]): Array<{
  directBooking: CalendarBooking;
  externalBooking: CalendarBooking;
  conflictType: 'overlap' | 'exact_match';
}> {
  const conflicts: Array<{
    directBooking: CalendarBooking;
    externalBooking: CalendarBooking;
    conflictType: 'overlap' | 'exact_match';
  }> = [];
  
  const directBookings = bookings.filter(b => !b.isExternal && b.status !== 'cancelled');
  const externalBookings = bookings.filter(b => b.isExternal && b.status !== 'cancelled');
  
  for (const directBooking of directBookings) {
    for (const externalBooking of externalBookings) {
      if (directBooking.listingId !== externalBooking.listingId) continue;
      
      const directStart = new Date(directBooking.startDate);
      const directEnd = new Date(directBooking.endDate);
      const externalStart = new Date(externalBooking.startDate);
      const externalEnd = new Date(externalBooking.endDate);
      
      // Check for overlap
      if (directStart < externalEnd && externalStart < directEnd) {
        const conflictType = (directStart.getTime() === externalStart.getTime() && 
                             directEnd.getTime() === externalEnd.getTime()) ? 'exact_match' : 'overlap';
        
        conflicts.push({
          directBooking,
          externalBooking,
          conflictType
        });
      }
    }
  }
  
  return conflicts;
}

/**
 * Gets booking source display text
 */
export function getBookingSourceText(booking: CalendarBooking): string {
  if (booking.isExternal) {
    return booking.source === 'airbnb' ? 'Airbnb' : 'External';
  }
  return 'Direct';
}

/**
 * Gets booking source icon
 */
export function getBookingSourceIcon(booking: CalendarBooking): string {
  if (booking.isExternal) {
    return booking.source === 'airbnb' ? '🏠' : '🔗';
  }
  return '📝';
}

/**
 * Gets a tooltip text for a calendar day with bookings
 */
export function getCalendarDayTooltip(day: CalendarDay): string {
  if (day.bookings.length === 0) {
    return `${formatDaySummaryDate(day.date)} - No bookings`;
  }
  
  if (day.bookings.length === 1) {
    const booking = day.bookings[0];
    const source = getBookingSourceText(booking);
    return `${formatDaySummaryDate(day.date)} - ${booking.listingTitle} (${mapBookingStatusToText(booking.status)}, ${source})`;
  }
  
  const directCount = day.bookings.filter(b => !b.isExternal).length;
  const externalCount = day.bookings.filter(b => b.isExternal).length;
  
  let tooltip = `${formatDaySummaryDate(day.date)} - ${day.bookings.length} bookings`;
  if (directCount > 0 && externalCount > 0) {
    tooltip += ` (${directCount} direct, ${externalCount} external)`;
  } else if (externalCount > 0) {
    tooltip += ` (${externalCount} external)`;
  }
  
  return tooltip;
}

/**
 * Formats a Date object to YYYY-MM-DD string in local timezone
 * This prevents timezone conversion issues when working with date pickers
 */
export function formatDateToLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}